import React, { useRef, useState } from "react";
import "./Forms.css";
import { useNavigate } from "react-router-dom";
import { Snackbar, Alert as MuiAlert } from "@mui/material"; // Import MuiAlert as Alert
import { Button, Box, Typography } from "@mui/material";
import { FiUpload } from "react-icons/fi";
import axiosInstance from "../axiosinstance";
import { validateGST, validateMobile, validateIFSC, validateAccountNumber } from "../helperFunctions";
export default function ClientManagementForm({ isSidebarExpanded }) {
  const [subsidaryData, setSubsidaryData] = useState({
    NameofCompany: "",
    NameofOwner: "",
    Address: "",
    MobileNo: "",
    Email: "",
    GstNO: "",
    InvoicingLimit: "",
    TagName: "",
    AccountNumber: "",
    AccountHolder: "",
    IFSCCode: "",
    Branch: "",
    PanNumber: "",
    VirtualPayementAddress: "",
  });

  const [isGstYes, setIsGstYes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [qrCodeFile, setQrCodeFile] = useState(null);
  const paymentQrInputRef = useRef(null);
  
  const handleCancel = () => {
    window.history.back();
  };
  const navigate = useNavigate();

  const handleGstChange = (e) => {
    const isYes = e.target.value === "yes";
    setIsGstYes(isYes);

    setSubsidaryData({
      ...subsidaryData,
      GstNO: isYes ? "" : "", // Clear GST number when "No" is selected
    });

    setErrors((prevErrors) => ({
      ...prevErrors,
      GstNO: "",
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setLogo(file);
    if (file) {
      setLogoPreview(URL.createObjectURL(file));
      setErrors((prev) => ({ ...prev, logo: "" })); // clear error
    } else {
      setLogoPreview(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Convert PAN and IFSC to uppercase while typing
    const updatedValue =
      name === "PanNumber" || name === "IFSCCode" ? value.toUpperCase() : value;

    // Remove errors when user starts typing
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));

    setSubsidaryData((prevData) => ({
      ...prevData,
      [name]: updatedValue, // Ensure correct key is updated
    }));
  };

  const handlePaymentQrChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setQrCodeFile(null);
      setErrors((prev) => ({ ...prev, qrCodeFile: "" }));
      return;
    }

    if (!file.type?.startsWith("image/")) {
      setQrCodeFile(null);
      setErrors((prev) => ({
        ...prev,
        qrCodeFile: "Only image files are allowed.",
      }));
      if (paymentQrInputRef.current) {
        paymentQrInputRef.current.value = "";
      }
      return;
    }

    setQrCodeFile(file);
    setErrors((prev) => ({ ...prev, qrCodeFile: "" }));
  };

  const handleClearPaymentQr = () => {
    setQrCodeFile(null);
    setErrors((prev) => ({ ...prev, qrCodeFile: "" }));
    if (paymentQrInputRef.current) {
      paymentQrInputRef.current.value = "";
    }
  };

  const formatFieldName = (fieldName) => {
    return fieldName.replace(/([A-Z])/g, " $1").trim(); // Adds spaces before uppercase letters
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
 
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
  const vpaRegex = /^[a-zA-Z0-9.\-_]+@[a-zA-Z0-9]+$/; // someone@upi format

const fieldRules = {
  NameofCompany: { required: true, max: 100, label: "Company Name" },
  NameofOwner: { required: true, max: 50, label: "Proprietor Name" },
  Address: { required: true, max: 250 },
  MobileNo: { required: true, custom: validateMobile, label: "Mobile Number" },
  Email: { required: true, pattern: emailRegex },
  GstNO: { required: isGstYes, label: "GST Number", custom: validateGST },
  InvoicingLimit: { required: true, numeric: true, label: "Invoicing Limit" },
  TagName: { required: true, max: 50, label: "Tag Name" },
  AccountNumber: { required: true, label: "Account Number", custom: validateAccountNumber },
  AccountHolder: { required: true, label: "Account Holder" },
  IFSCCode: {
    required: true,
    label: "IFSC Code",
    custom: validateIFSC
  },
  Branch: { required: false },
  PanNumber: { required: true, pattern: panRegex, label: "PAN Number" },
  VirtualPayementAddress: {
    required: false,
    pattern: vpaRegex,
    max: 100,
    label: "Virtual Payment Address",
  },
};


  const formatField = (field) =>
  field.replace(/([A-Z])/g, " $1").trim();

const validateForm = () => {
  const errors = {};

  Object.keys(fieldRules).forEach((field) => {
    const value = subsidaryData[field]?.toString().trim();
    const rules = fieldRules[field];
    const label = rules.label || formatField(field);
    // Required
    if (rules.required && !value) {
      errors[field] = `${label} is required`;
      return;
    }

    // Max length
    if (value && rules.max && value.length > rules.max) {
      errors[field] = `${label} cannot exceed ${rules.max} characters`;
      return;
    }

    // Pattern
    if (value && rules.pattern && !rules.pattern.test(value)) {
      errors[field] = `Invalid ${label}`;
      return;
    }

    // Numeric
    if (value && rules.numeric && isNaN(value)) {
      errors[field] = `${label} must be a number`;
      return;
    }

    // Custom validator (mobile)
    if (value && rules.custom) {
      const customError = rules.custom(value);
      if (customError) {
        errors[field] = customError;
      }
    }
  });

  // File validation
  if (qrCodeFile && !qrCodeFile.type?.startsWith("image/")) {
    errors.qrCodeFile = "Only image files are allowed.";
  }

  if (!logo) {
    errors.logo = "Please upload logo";
  }
  
  return errors;
};
  // Old
  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   console.log("Submitting form..."); // Debugging

  //   const validationErrors = {};

  //   // Validate required fields (except optional ones)
  //   Object.keys(subsidaryData).forEach((key) => {
  //     if (
  //       !subsidaryData[key] &&
  //       ![
  //         "GstNO",
  //         "AccountNumber",
  //         "Branch",
  //         "AccountHolder",
  //         "IFSCCode",
  //         "VirtualPayementAddress",
  //         "AccountNumber",
  //       ].includes(key)
  //     ) {
  //       validationErrors[key] = `${key.replace(/([A-Z])/g, " $1")} is required`;
  //     }
  //   });

  //   if (qrCodeFile && !qrCodeFile.type?.startsWith("image/")) {
  //     validationErrors.qrCodeFile = "Only image files are allowed.";
  //   }

  //   // Validate email format
  //   if (subsidaryData.Email && !emailRegex.test(subsidaryData.Email)) {
  //     validationErrors.Email = "Invalid email format";
  //   }
  //   // Validate Name of Company (required)
  //   if (subsidaryData.NameofCompany.length > 100) {
  //     validationErrors.NameofCompany =
  //       "Company Name cannot exceed 100 characters";
  //   }
  //   if (subsidaryData.NameofOwner.length > 50) {
  //     validationErrors.NameofOwner =
  //       "Proprietor Name cannot exceed 50 characters";
  //   }
  //   if (subsidaryData.TagName.length > 50) {
  //     validationErrors.TagName = "Tag Name cannot exceed 50 characters";
  //   }
  //   if (subsidaryData.Address.length > 250) {
  //     validationErrors.Address = "Address cannot exceed 250 characters";
  //   }
  //   // Validate Account Number (required)
  //   if (!subsidaryData.AccountNumber) {
  //     validationErrors.AccountNumber = "Account Number is required";
  //   }
  //   if (!subsidaryData.AccountHolder) {
  //     validationErrors.AccountHolder = "Account Holder is required";
  //   }
  //   if (!subsidaryData.IFSCCode) {
  //     validationErrors.IFSCCode = "IFSC Code is required";
  //   }
  //   // Validate VPA format (only if entered)
  //   if (
  //     subsidaryData.VirtualPayementAddress &&
  //     !vpaRegex.test(subsidaryData.VirtualPayementAddress)
  //   ) {
  //     validationErrors.VirtualPayementAddress =
  //       "Invalid VPA format (e.g., someone@upi)";
  //   }
  //   if (subsidaryData.VirtualPayementAddress.length > 100) {
  //     validationErrors.VirtualPayementAddress =
  //       "VPA cannot exceed 100 characters";
  //   }

  //   if (!logo) {
  //     validationErrors.logo = "Please upload logo";
  //   }

  //   // Validate PAN format
  //   if (subsidaryData.PanNumber && !panRegex.test(subsidaryData.PanNumber)) {
  //     validationErrors.PanNumber = "Invalid PAN format (e.g., ABCDE1234F)";
  //   }

  //   // Validate mobile number
  //   // if (subsidaryData.MobileNo && !mobileRegex.test(subsidaryData.MobileNo)) {
  //   //   validationErrors.MobileNo =
  //   //     "Invalid mobile number (10 digits, starts with 6-9)";
  //   // }
  //   const mobileErr = validateMobile(subsidaryData.MobileNo);
  //   if (mobileErr) validationErrors.MobileNo = mobileErr;

  //   // GST validation if required
  //   if (isGstYes && !subsidaryData.GstNO) {
  //     validationErrors.GstNO = "GST Number is required";
  //   }

  //   if (Object.keys(validationErrors).length > 0) {
  //     console.log("Validation failed", validationErrors); // Debugging
  //     setErrors(validationErrors);
  //     return;
  //   }

  //   setIsLoading(true);
  //   try {

  //     console.log("Sending API request..."); // Debugging
  //     const formData = new FormData();
  //     formData.append("sub_name", subsidaryData.NameofCompany);
  //     formData.append("address", subsidaryData.Address);
  //     formData.append("owner", subsidaryData.NameofOwner);
  //     formData.append("contact_no", subsidaryData.MobileNo);
  //     formData.append("email_id", subsidaryData.Email);
  //     formData.append("hasGST", isGstYes ? "Yes" : "No");
  //     formData.append("gst_no", isGstYes ? subsidaryData.GstNO : "");
  //     formData.append("invoicing_limit", subsidaryData.InvoicingLimit);
  //     formData.append("tag_name", subsidaryData.TagName);
  //     formData.append("account_number", subsidaryData.AccountNumber);
  //     formData.append("account_holder", subsidaryData.AccountHolder);
  //     formData.append("ifsc_code", subsidaryData.IFSCCode);
  //     formData.append("branch", subsidaryData.Branch);
  //     formData.append("pan_no", subsidaryData.PanNumber);
  //     formData.append("vpa", subsidaryData.VirtualPayementAddress);

  //     // append the file
  //     if (logo) {
  //       formData.append("logo", logo); // <-- field name the backend should read
  //     }
  //     if (qrCodeFile) {
  //       formData.append("qr_code", qrCodeFile);
  //     }

  //     const response = await axiosInstance.post(
  //       `/api/create_subsidiary`,
  //       formData
  //       // Remove headers - axiosInstance handles them automatically
  //     );

  //     if (response.status === 200) {
  //       setSnackbarMessage("Subsidiary added successfully");
  //       setSnackbarSeverity("success");
  //       setSnackbarOpen(true);
  //       setSubsidaryData({
  //         NameofCompany: "",
  //         NameofOwner: "",
  //         Address: "",
  //         MobileNo: "",
  //         Email: "",
  //         GstNO: "",
  //         InvoicingLimit: "",
  //         TagName: "",
  //         AccountNumber: "",
  //         AccountHolder: "",
  //         IFSCCode: "",
  //         Branch: "",
  //         PanNumber: "",
  //         VirtualPayementAddress: "",
  //       });
  //       setIsGstYes(false);
  //       setLogo(null);
  //       setLogoPreview(null);
  //       setQrCodeFile(null);
  //       if (paymentQrInputRef.current) {
  //         paymentQrInputRef.current.value = "";
  //       }
  //       setTimeout(() => {
  //         navigate("/subsidary");
  //       }, 2000);
  //     } else {
  //       throw new Error("Failed to add subsidiary");
  //     }
  //   } catch (error) {
  //     console.error("API Error:", error); // Debugging
  //     if (error.response?.status === 400) {
  //       setSnackbarMessage(
  //         error.response?.data?.error ||
  //           "This firm has already reached the maximum allowed number of subsidiaries. Please upgrade your subscription to add more subsidiaries."
  //       );
  //     } else {
  //       setSnackbarMessage(error.response?.data?.message || error.message);
  //     }

  //     setSnackbarSeverity("error");
  //     setSnackbarOpen(true);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };
  // New
const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Submitting form..."); // Debugging
    
    const validationErrors = validateForm()
    
    // Validate mobile number
    // if (subsidaryData.MobileNo && !mobileRegex.test(subsidaryData.MobileNo)) {
    //   validationErrors.MobileNo =
    //     "Invalid mobile number (10 digits, starts with 6-9)";
    // }
    
    

    // GST validation if required
  

    if (Object.keys(validationErrors).length > 0) {
      console.log("Validation failed", validationErrors); // Debugging
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);
    try {

      console.log("Sending API request..."); // Debugging
      const formData = new FormData();
      formData.append("sub_name", subsidaryData.NameofCompany);
      formData.append("address", subsidaryData.Address);
      formData.append("owner", subsidaryData.NameofOwner);
      formData.append("contact_no", subsidaryData.MobileNo);
      formData.append("email_id", subsidaryData.Email);
      formData.append("hasGST", isGstYes ? "Yes" : "No");
      formData.append("gst_no", isGstYes ? subsidaryData.GstNO : "");
      formData.append("invoicing_limit", subsidaryData.InvoicingLimit);
      formData.append("tag_name", subsidaryData.TagName);
      formData.append("account_number", subsidaryData.AccountNumber);
      formData.append("account_holder", subsidaryData.AccountHolder);
      formData.append("ifsc_code", subsidaryData.IFSCCode);
      formData.append("branch", subsidaryData.Branch);
      formData.append("pan_no", subsidaryData.PanNumber);
      formData.append("vpa", subsidaryData.VirtualPayementAddress);

      // append the file
      if (logo) {
        formData.append("logo", logo); // <-- field name the backend should read
      }
      if (qrCodeFile) {
        formData.append("qr_code", qrCodeFile);
      }

      const response = await axiosInstance.post(
        `/api/create_subsidiary`,
        formData
        // Remove headers - axiosInstance handles them automatically
      );

      if (response.status === 200) {
        setSnackbarMessage("Subsidiary added successfully");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setSubsidaryData({
          NameofCompany: "",
          NameofOwner: "",
          Address: "",
          MobileNo: "",
          Email: "",
          GstNO: "",
          InvoicingLimit: "",
          TagName: "",
          AccountNumber: "",
          AccountHolder: "",
          IFSCCode: "",
          Branch: "",
          PanNumber: "",
          VirtualPayementAddress: "",
        });
        setIsGstYes(false);
        setLogo(null);
        setLogoPreview(null);
        setQrCodeFile(null);
        if (paymentQrInputRef.current) {
          paymentQrInputRef.current.value = "";
        }
        setTimeout(() => {
          navigate("/subsidary");
        }, 2000);
      } else {
        throw new Error("Failed to add subsidiary");
      }
    } catch (error) {
      console.error("API Error:", error); // Debugging
      if (error.response?.status === 400) {
        setSnackbarMessage(
          error.response?.data?.error ||
            "This firm has already reached the maximum allowed number of subsidiaries. Please upgrade your subscription to add more subsidiaries."
        );
      } else {
        setSnackbarMessage(error.response?.data?.message || error.message);
      }

      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSubsChange = (field, value) => {
    let next = value;

    if (field === "MobileNo") {
      next = (value || "").replace(/\D/g, "").slice(0, 10); // digits only, max 10
    }

    setSubsidaryData((prev) => ({ ...prev, [field]: next }));

    if (field === "MobileNo") {
      setErrors((prev) => ({ ...prev, MobileNo: validateMobile(next) }));
    }
  };

  return (
    <div
      className={`table-container subsidiary-form-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="table-container-content form-main-body subsidiary-form-content">
        <div className="subsidiary-page-heading">
          <h1 className="form-title jost-bold">New Subsidiary</h1>
          <p className="subsidiary-page-subtitle">
            Add subsidiary details, tax information and banking details.
          </p>
        </div>
        <div className="subsidiary-form-card">
        <div className="form-body-subsidary subsidiary-form-body">
          <form
            onSubmit={handleSubmit}
            className="form-box subsidiary-form-box"
            encType="multipart/form-data"
          >
            {/* Name of Company */}
            <div className="form-body-container subsidiary-form-sections">
              <section className="subsidiary-form-section">
                <div className="subsidiary-section-header">
                  <div>
                    <h2 className="subsidiary-section-title">Basic Details</h2>
                    <p className="subsidiary-section-description">
                      Add core company, owner and contact information.
                    </p>
                  </div>
                </div>
              <div className="form-group">
                <label className="form-label">
                  Name of Company <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="NameofCompany"
                  className="input-field"
                  placeholder="Enter Name of Company"
                  value={subsidaryData.NameofCompany}
                  onChange={handleInputChange}
                  autoFocus
                  maxLength={100}
                />
                {errors.NameofCompany && (
                  <p className="error">{errors.NameofCompany}</p>
                )}
              </div>

              {/* Name of Owner */}
              <div className="form-group">
                <label className="form-label">
                  Name of Proprietor / Owner <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="NameofOwner"
                  className="input-field"
                  placeholder="Enter Name of Owner"
                  value={subsidaryData.NameofOwner}
                  onChange={handleInputChange}
                  maxLength={50}
                />
                {errors.NameofOwner && (
                  <p className="error">{errors.NameofOwner}</p>
                )}
              </div>

              {/* Address */}
              <div className="form-group subsidiary-field-span-2">
                <label className="form-label">
                  Address <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="Address"
                  className="input-field"
                  placeholder="Enter Address"
                  value={subsidaryData.Address}
                  onChange={handleInputChange}
                  maxLength={250}
                />
                {errors.Address && <p className="error">{errors.Address}</p>}
              </div>

              {/* Mobile No */}
              <div className="form-group">
                <label className="form-label">
                  Mobile No <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  name="MobileNo"
                  className="input-field"
                  placeholder="Enter Mobile Number"
                  value={subsidaryData.MobileNo || ""}
                  inputMode="numeric"
                  maxLength={10}
                  onChange={(e) => handleSubsChange("MobileNo", e.target.value)}
                  required
                />
                {errors.MobileNo && <p className="error">{errors.MobileNo}</p>}
              </div>

              {/* Email */}
              <div className="form-group">
                <label className="form-label">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  name="Email"
                  className="input-field"
                  placeholder="Enter Email"
                  value={subsidaryData.Email}
                  onChange={handleInputChange}
                />
                {errors.Email && <p className="error">{errors.Email}</p>}
              </div>

              {/* Tag Name */}
              <div className="form-group">
                <label className="form-label">
                  Tag Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="TagName"
                  className="input-field"
                  placeholder="Enter Tag Name"
                  value={subsidaryData.TagName}
                  onChange={handleInputChange}
                  maxLength={50}
                />
                {errors.TagName && <p className="error">{errors.TagName}</p>}
              </div>
              </section>

              <section className="subsidiary-form-section">
                <div className="subsidiary-section-header">
                  <div>
                    <h2 className="subsidiary-section-title">
                      Tax & Billing Details
                    </h2>
                    <p className="subsidiary-section-description">
                      Keep GST preferences and invoicing rules aligned.
                    </p>
                  </div>
                </div>
              {/* GST */}
              <div className="form-group subsidiary-field-span-2">
                <label className="form-label">
                  GST No. <span className="required">*</span>
                </label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="GstNO"
                      value="yes"
                      checked={isGstYes}
                      onChange={handleGstChange}
                    />
                    Yes
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="GstNO"
                      value="no"
                      checked={!isGstYes}
                      onChange={handleGstChange}
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Display GST input field only if GST is selected */}
              {isGstYes && (
                <div className="form-group subsidiary-field-span-2">
                  <label className="form-label"></label>
                  <input
                    type="text"
                    name="GstNO"
                    className="input-field"
                    placeholder="Enter GST Number"
                    value={subsidaryData.GstNO}
                    onChange={handleInputChange}
                  />
                  {errors.GstNO && <p className="error">{errors.GstNO}</p>}
                </div>
              )}

              {/* Invoicing Limit */}
              <div className="form-group">
                <label className="form-label">
                  Invoicing Limit <span className="required">*</span>
                </label>
                <input
                  type="number"
                  name="InvoicingLimit"
                  className="input-field"
                  placeholder="Enter Invoicing Limit"
                  value={subsidaryData.InvoicingLimit}
                  onChange={handleInputChange}
                />
                {errors.InvoicingLimit && (
                  <p className="error">{errors.InvoicingLimit}</p>
                )}
              </div>
              </section>

              <section className="subsidiary-form-section">
                <div className="subsidiary-section-header">
                  <div>
                    <h2 className="subsidiary-section-title">
                      Bank / Payment Details
                    </h2>
                    <p className="subsidiary-section-description">
                      Add banking, branch and payment identity details.
                    </p>
                  </div>
                </div>
              <div className="form-group">
                <label className="form-label">
                  Account Number <span className="required">*</span>
                </label>
                <input
                  type="number"
                  name="AccountNumber"
                  className="input-field"
                  placeholder="Enter Account Number"
                  value={subsidaryData.AccountNumber}
                  onChange={handleInputChange}
                />
                {errors.AccountNumber && (
                  <p className="error">{errors.AccountNumber}</p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">
                  Account Holder <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="AccountHolder"
                  className="input-field"
                  placeholder="Enter Account Holder"
                  value={subsidaryData.AccountHolder}
                  onChange={handleInputChange}
                />
                {errors.AccountHolder && (
                  <p className="error">{errors.AccountHolder}</p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">
                  IFSC Code <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="IFSCCode"
                  className="input-field"
                  placeholder="Enter IFSC Code"
                  value={subsidaryData.IFSCCode}
                  onChange={handleInputChange}
                />
                {errors.IFSCCode && <p className="error">{errors.IFSCCode}</p>}
              </div>
              <div className="form-group">
                <label className="form-label">Bank Branch</label>
                <input
                  type="text"
                  name="Branch" // ✅ Corrected to match state key
                  className="input-field"
                  placeholder="Enter Bank Branch"
                  value={subsidaryData.Branch}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  PAN Number <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="PanNumber" // Ensure it matches the state key
                  className="input-field"
                  placeholder="Enter PAN Number"
                  value={subsidaryData.PanNumber} // Correct state reference
                  onChange={handleInputChange}
                />
                {errors.PanNumber && (
                  <p className="error">{errors.PanNumber}</p>
                )}{" "}
              </div>

              <div className="form-group">
                <label className="form-label">
                  UPI ID<span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="VirtualPayementAddress"
                  className="input-field"
                  placeholder="Enter UPI ID"
                  value={subsidaryData.VirtualPayementAddress}
                  onChange={handleInputChange}
                  maxLength={100}
                />
                {errors.VirtualPayementAddress && (
                  <p className="error">{errors.VirtualPayementAddress}</p>
                )}
              </div>
              </section>

              <section className="subsidiary-form-section subsidiary-form-section-last">
                <div className="subsidiary-section-header">
                  <div>
                    <h2 className="subsidiary-section-title">Uploads</h2>
                    <p className="subsidiary-section-description">
                      Manage logo and QR assets without changing existing upload
                      behavior.
                    </p>
                  </div>
                </div>
              <div className="form-group subsidiary-upload-group">
                <label className="form-label">
                  Upload Logo <span className="required">*</span>
                </label>

                {/* Hidden file input */}
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="upload-logo"
                  type="file"
                  onChange={handleFileChange}
                />

                {/* Upload button with react-icon */}
                <label htmlFor="upload-logo">
                  <Button
                    variant="outlined"
                    component="span"
                    className="upload-file-btn"
                    startIcon={<FiUpload className="upload-file-icon" />}
                  >
                    {logo ? "Change Logo" : "Upload Logo"}
                  </Button>
                </label>

                {/* Validation error */}
                {errors.logo && (
                  <Typography variant="caption" color="error" display="block">
                    {errors.logo}
                  </Typography>
                )}

                {/* Thumbnail preview */}
                {logoPreview && (
                  <Box
                    mt={0}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 2,
                      p: "2px",
                      display: "inline-block",
                    }}
                  >
                    <img
                      src={logoPreview}
                      alt="Logo Preview"
                      style={{ width: 100, height: 50, objectFit: "contain" }}
                    />
                  </Box>
                )}
              </div>
              <div className="form-group subsidiary-upload-group">
                <label className="form-label">Payment QR Code</label>
                <div className="upload-field-wrap">
                  <input
                    ref={paymentQrInputRef}
                    type="file"
                    id="payment_qr"
                    name="qr_code"
                    accept="image/*"
                    onChange={handlePaymentQrChange}
                    style={{ display: "none" }}
                  />
                  <label htmlFor="payment_qr">
                    <Button
                      variant="outlined"
                      component="span"
                      className="upload-file-btn"
                      startIcon={<FiUpload className="upload-file-icon" />}
                    >
                      {qrCodeFile
                        ? "Change Payment QR Code"
                        : "Upload Payment QR Code"}
                    </Button>
                  </label>
                  <p className="upload-helper-text">
                    Upload UPI QR image (optional).
                  </p>
                  {errors.qrCodeFile && <p className="error">{errors.qrCodeFile}</p>}
                  {qrCodeFile && (
                    <div className="upload-selected-row">
                      <span className="upload-selected-text">
                        Selected: {qrCodeFile.name}
                      </span>
                      <button
                        type="button"
                        className="upload-remove-btn"
                        onClick={handleClearPaymentQr}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
              </section>
            </div>
          </form>
        </div>
      <div className="form-footer subsidiary-form-footer">
        <div className="button-group">
          <button
            type="button"
            className="btn btn-cancel"
            onClick={handleCancel}
          >
            CANCEL
          </button>
          <button type="submit" className="btn btn-save" onClick={handleSubmit}>
            SAVE
          </button>
        </div>
      </div>
      </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ transform: "translateY(40px)", marginLeft: "15px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
}
